import { Button } from '@mui/material';
import { useField, Field } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Spacer } from '../../types/form-fields';
import { FormRow, InputField } from '../fields';
import { FormBlock } from './common';

export const isSpacerField = (field: FormField): field is Spacer => {
  return field.type === FieldType.Spacer;
};

export const defaultSpacerFieldProps: Spacer = {
  type: FieldType.Spacer,
  size: 12,
};

type SpacerFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const spacerSizeSchema = Yup.number()
  .required('Required')
  .max(100, 'Should be less than 100')
  .min(0, 'Should be more than 0')
  .typeError('Required');

const SpacerFieldSettings: FC<SpacerFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const { t } = useTranslation();

  return (
    <SpacerFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.size`}
          component={InputField}
          label={t('pages.campaign.checkboxField.size')}
          type="number"
          validate={(value: string) => validate(value, spacerSizeSchema)}
        />
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </SpacerFieldSettingsWrapper>
  );
};

type SpacerFieldProps = {
  name: string;
  remove?: () => void;
};

export const SpacerField: FC<SpacerFieldProps> = ({ name, remove }) => {
  const [field] = useField<Spacer>(name);
  const dispatch = useAppDispatch();

  const { size } = field.value;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <SpacerFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <SpacerContainer size={size} />
    </FormBlock>
  );
};

const SpacerFieldSettingsWrapper = styled.div``;

const SpacerContainer = styled.div<{ size: number }>`
  width: 100%;
  height: ${(p) => p.size}px;
`;
